// ─────────────────────────────────────────────
// src/pages/FAQPage.jsx
// ─────────────────────────────────────────────
import { useState, useEffect } from "react";
import { StarField, GlassCard, Btn, SectionLabel } from "../components/UI";
import Footer from "../components/Footer";
import { FAQ_DATA } from "../data/constants";

const CATEGORY_ICONS = {
  "Getting Started":  "🚀",
  "Program Details":  "📅",
  "Our Instructors":  "🧑‍🏫",
  "Partner With Us":  "🤝",
};

export default function FAQPage({ setPage }) {
  const [openKey, setOpenKey] = useState(null);

  // FAQPage structured data
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = '__faq-page-schema';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "url": "https://www.marsacademy.ai/faq",
      "mainEntity": FAQ_DATA.flatMap(g => g.items).map(f => ({
        "@type": "Question",
        "name": f.q,
        "acceptedAnswer": { "@type": "Answer", "text": f.a }
      }))
    });
    document.head.appendChild(script);
    return () => document.getElementById('__faq-page-schema')?.remove();
  }, []);

  return (
    <div>
      {/* Header */}
      <section style={{ padding:"8rem 2rem 3.5rem", textAlign:"center", position:"relative", overflow:"hidden" }}>
        <StarField count={40} />
        <div style={{ position:"relative", zIndex:2, maxWidth:680, margin:"0 auto" }}>
          <SectionLabel color="#22C55E">FAQ</SectionLabel>
          <h1 style={{ fontFamily:"'Fredoka One',cursive", fontSize:"clamp(2rem,5vw,3.2rem)", lineHeight:1.2, marginBottom:"1rem" }}>
            Everything You're Wondering About 🙋
          </h1>
          <p style={{ color:"rgba(255,255,255,0.55)", fontSize:"1rem", fontWeight:600, lineHeight:1.8, maxWidth:560, margin:"0 auto" }}>
            From signing up to what happens in the classroom, here's what most parents and schools ask us before joining Mars Academy. Can't find what you need? We're one email away.
          </p>
        </div>
      </section>

      {/* FAQ groups */}
      <section style={{ padding:"1rem 2rem 5rem", maxWidth:760, margin:"0 auto" }}>
        {FAQ_DATA.map((group, gi) => (
          <div key={gi} style={{ marginBottom:"2.6rem" }}>
            <div style={{ display:"flex", alignItems:"center", gap:"0.6rem", marginBottom:"0.8rem" }}>
              <span style={{ fontSize:"1.25rem" }}>{CATEGORY_ICONS[group.category] || "❓"}</span>
              <h2 style={{ fontFamily:"'Fredoka One',cursive", fontSize:"1.15rem", color:"#FFD93D" }}>{group.category}</h2>
            </div>
            <GlassCard hover={false} style={{ padding:"0.3rem 1.6rem" }}>
              {group.items.map((f, qi) => {
                const key = `${gi}-${qi}`;
                const isOpen = openKey === key;
                return (
                  <div key={key} style={{ borderBottom: qi === group.items.length - 1 ? "none" : "1px solid rgba(255,255,255,0.08)" }}>
                    <button
                      onClick={() => setOpenKey(isOpen ? null : key)}
                      style={{ width:"100%", background:"none", border:"none", color:"#fff", textAlign:"left", padding:"1.15rem 0", fontFamily:"inherit", fontSize:"0.97rem", fontWeight:700, cursor:"pointer", display:"flex", justifyContent:"space-between", alignItems:"center", gap:"1rem" }}
                    >
                      <span>{f.q}</span>
                      <span style={{ color:"#FFD93D", fontSize:"1.3rem", flexShrink:0, transition:"transform 0.2s", transform:isOpen?"rotate(45deg)":"none" }}>+</span>
                    </button>
                    {isOpen && (
                      <p style={{ color:"rgba(255,255,255,0.55)", fontSize:"0.9rem", lineHeight:1.75, paddingBottom:"1.2rem", fontWeight:600 }}>
                        {f.a}
                      </p>
                    )}
                  </div>
                );
              })}
            </GlassCard>
          </div>
        ))}

        {/* Still have questions */}
        <div style={{ background:"linear-gradient(135deg,rgba(255,107,107,0.12),rgba(155,92,246,0.12))", border:"1.5px solid rgba(255,255,255,0.1)", borderRadius:24, padding:"2.4rem 2rem", textAlign:"center", marginTop:"1rem" }}>
          <h3 style={{ fontFamily:"'Fredoka One',cursive", fontSize:"1.3rem", marginBottom:"0.6rem" }}>Still have questions?</h3>
          <p style={{ color:"rgba(255,255,255,0.55)", fontSize:"0.92rem", fontWeight:600, lineHeight:1.7, marginBottom:"1.4rem", maxWidth:440, margin:"0 auto 1.4rem" }}>
            Reach out and a real person from our team will get back to you.
          </p>
          <div style={{ display:"flex", gap:"0.8rem", justifyContent:"center", flexWrap:"wrap" }}>
            <Btn onClick={() => window.location.href = "mailto:info@marsacademy.ai"} variant="primary">📧 info@marsacademy.ai</Btn>
            <Btn onClick={() => setPage("home")} variant="secondary">🏠 Back to Home</Btn>
          </div>
        </div>
      </section>

      <Footer setPage={setPage} />
    </div>
  );
}
